<script>
import { reactive } from 'vue'
import { mapState, mapMutations, mapActions } from 'vuex'
import * as def from '@/lib/definition'
import { datestr, getSize } from '@/lib/common'
import imageViewer from '@/components/image-viewer.vue'
import textEditor from '@/components/text-editor.vue'

export default {
  components: {
    imageViewer,
    textEditor
  },
  props: [
    'pageId',
    'articleId'
  ],
  setup: () => {
    return reactive({
      def,
      images: []
    })
  },
  computed: {
    ...mapState([
      'pages'
    ]),
    article () {
      if (!this.pages[this.pageId]) {
        return {}
      }
      return this.pages[this.pageId].articles[this.articleId] || {}
    },
    url () {
      return `${def.host}/${this.pageId}/${this.articleId}`
    },
    exp () {
      const r = {
        ...this.article
      }
      delete r.id
      def.dateObjectItems.forEach(key => {
        if (r[key]) {
          r[key] = datestr(r[key], datestr.dateTime)
        }
      })
      return r
    },
    details () {
      return this.article.details || false
    },
    sysInfoItems () {
      const r = {}
      Object.keys(def.sysInfoItems).forEach(key => {
        if (this.exp[key]) {
          r[key] = this.exp[key]
        }
      })
      return r
    }
  },
  unmounted () {
    this.setEditor(false)
  },
  methods: {
    ...mapMutations([
      'setEditor'
    ]),
    ...mapActions([
      'fetchArticle'
    ]),
    getFileName (image) {
      const fileName = image && image.metadata && image.metadata.customMetadata
        ? image.metadata.customMetadata.fileName
        : ''
      return fileName
    },
    getSize (image) {
      const size = image && image.metadata ? image.metadata.size : 0
      return getSize(size)
    },
    setMetadata (metadata, i) {
      this.images[i] = { metadata }
    }
  }
}
</script>

<template>
  <div
    :key="article.id"
    class="article"
  >
    <header>
      <h1 class="is-size-3">{{ article.id }}</h1>
    </header>
    <dl class="dl-column">
      <dt>URL</dt>
      <dd>
        <a
          :href="url"
          target="_blank"
          class="level-left button is-plain"
        >
          <span>{{ url }}</span>
          <i class="material-icons">open_in_new</i>
        </a>
      </dd>
      <dt>{{ def.itemNames['title'] }}</dt>
      <dd class="is-size-2">{{ article.title }}</dd>
      <dt>{{ def.itemNames['publication_date'] }}</dt>
      <dd>{{ exp.publication_date }}</dd>
      <!--<dt>{{ def.itemNames['description'] }}</dt>
      <dd>{{ exp.description }}</dd>-->
      <template v-if="article.thumbnail">
        <dt>{{ def.itemNames['thumbnail'] }}</dt>
        <image-viewer
          class="thumbnail article-image"
          :key="article.thumbnail"
          :fullPath="article.thumbnail"
          tag="dd"
        />
      </template>
      <template v-if="article.images && article.images.length">
        <dt>{{ def.itemNames['images'] }}</dt>
        <dd class="article-images image-list">
          <ul class="level-left">
            <image-viewer
              v-for="image in article.images"
              :key="image"
              :fullPath="image"
              class="article-image"
              tag="li"
            />
          </ul>
        </dd>
      </template>
      <template v-if="article.paragraphs">
        <dt>{{ def.itemNames['text'] }}</dt>
        <dd>
          <text-editor
            :key="article.paragraphs"
            :paragraphs="article.paragraphs"
            mode="view"
          />
        </dd>
      </template>
      <template v-if="article.text">
        <dt>{{ def.itemNames['text'] }}</dt>
        <dd v-html="article.text"></dd>
      </template>
      <template v-if="details">
        <template
          v-for="detail in details"
          :key="detail.name"
        >
            <dt>{{ detail.name }}</dt>
            <dd v-if="detail.name==='URL'">
              <a
                :href="detail.content"
                target="_blank"
                rel="noopener noreferrer"
                class="button is-plain"
              >
                <span>{{ detail.content }}</span>
                <i class="material-icons">open_in_new</i>
              </a>
            </dd>
            <dd v-else>{{ detail.content }}</dd>
        </template>
      </template>
      <template v-if="article.tags">
        <dt>{{ def.itemNames['tags'] }}</dt>
        <dd>
          <ul class="tag-list level-left is-mobile">
            <li v-for="tag in article.tags" :key="tag">
              <span>{{ tag }}</span>
            </li>
          </ul>
        </dd>
      </template>
    </dl>
    <footer class="level">
      <div class="level-left">
      <dl
        v-if="sysInfoItems['created']"
        class="dl-row"
      >
        <dt>{{ def.itemNames['created_by'] }}</dt>
        <dd>{{ sysInfoItems['created_by'] }}</dd>
        <dt>{{ def.itemNames['created'] }}</dt>
        <dd>{{ sysInfoItems['created'] }}</dd>
      </dl>
      <dl
        v-if="sysInfoItems['modified']"
        class="dl-row"
      >
        <dt>{{ def.itemNames['modified_by'] }}</dt>
        <dd>{{ sysInfoItems['modified_by'] }}</dd>
        <dt>{{ def.itemNames['modified'] }}</dt>
        <dd>{{ sysInfoItems['modified'] }}</dd>
      </dl>
      </div>
      <button
        @click="setEditor(article)"
        class="button"
      >
        <i class="material-icons">edit</i>
        <span>記事を編集する</span>
      </button>
    </footer>
  </div>
</template>

<style lang="scss" scoped>
@use "../styles/variables";
.article {
  border-left: 2px solid variables.$gray-light;
  padding: 1rem;
  min-height: 100%;
  background-color: variables.$gray-lighter;
  > dl {
    margin-bottom: 9rem;
  }
  .article-image {
    max-width: 10rem;
    max-height: 15rem;
    margin: 1rem .5rem;
  }
  footer {
    position: fixed;
    bottom: 0;
    right: 0;
    border-top: solid 1px variables.$gray-light;
    background-color: variables.$gray-lighter;
    width: calc(100vw - .25rem - #{variables.$width-page-list} - #{variables.$width-article-list});
    padding: 1rem;
    flex-wrap: wrap;
    button {
      margin: 1rem;
    }
    dl {
      margin: 0;
      &:first-child {
        margin-right: 1rem;
      }
      dt {
        width: 6rem;
      }
      dd {
        width: 10rem;
        margin-left: 5rem;
      }
    }
  }
}
</style>
